// src/WallpaperScene.js
import React, { useRef } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { TextureLoader } from 'three';
import { Cog } from './Cog'; // Your spinning cog component

function FullscreenPlane() {
  const { viewport, size } = useThree();
  const aspect = size.width / size.height;
  const texture = useLoader(TextureLoader, '/wallpaper.jpg');
  const materialRef = useRef();

  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
  });

  return (
    <mesh scale={[aspect > 1 ? aspect : 1, aspect > 1 ? 1 : 1 / aspect, 1]}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={materialRef}
        uniforms={{
          uTexture: { value: texture },
          uTime: { value: 0 }
        }}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform sampler2D uTexture;
  uniform float uTime;
  varying vec2 vUv;

  float rand(vec2 co) {
    return fract(sin(dot(co.xy, vec2(12.9898,78.233))) * 43758.5453);
  }

  float noise(vec2 st) {
    vec2 i = floor(st);
    vec2 f = fract(st);
    float a = rand(i);
    float b = rand(i + vec2(1.0, 0.0));
    float c = rand(i + vec2(0.0, 1.0));
    float d = rand(i + vec2(1.0, 1.0));
    vec2 u = f*f*(3.0-2.0*f);
    return mix(a, b, u.x) +
           (c - a)*u.y*(1.0 - u.x) +
           (d - b)*u.x*u.y;
  }

  void main() {
    vec2 uv = vUv;
    vec4 texColor = texture2D(uTexture, uv);

    float r = texColor.r;
    float g = texColor.g;
    float b = texColor.b;

    bool isFire = (r > 0.4 && g > 0.2 && b < 0.2);
    bool isSmoke = (abs(r - g) < 0.1 && abs(g - b) < 0.1 && r > 0.3 && r < 0.7);

    if (isFire || isSmoke) {
      float distort = noise(uv * 10.0 + uTime * 0.5);
      uv.x += 0.015 * sin(uTime + uv.y * 10.0) * distort;
      uv.y += 0.015 * cos(uTime + uv.x * 5.0) * distort;

      texColor = texture2D(uTexture, uv);
      float glow = 0.25 * sin(uTime * 4.0 + uv.y * 8.0);
      texColor.rgb += vec3(glow * 1.0, glow * 0.4, 0.1);
    }

    gl_FragColor = texColor;
  }
`;

export default function WallpaperScene() {
  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      {/* Canvas background */}
      <Canvas camera={{ position: [0, 0, 1], fov: 75 }} gl={{ alpha: false }}>
        <color attach="background" args={['black']} />
        <FullscreenPlane />
      </Canvas>

      {/* Foreground Canvas (Spinning Cog) */}
      <Canvas
        style={{
          position: 'absolute',
          top: 0,
          left: -365,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 5,
        }}
      >
        <ambientLight />
        <Cog position={[-3.1, 1.2, 0]} size={1.2} />
      </Canvas>

      {/* Text Overlay */}
      <div
        style={{
          position: 'absolute',
          top: '30%',
          left: '15%',
          fontSize: '5rem',
          fontWeight: 'bold',
          fontFamily: 'sans-serif',
          color: 'white',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
        }}
      >
        | Project 404
      </div>

      {/* Subtitle Block with Dynamic Blur per Line */}
      <div
        style={{
          position: 'absolute',
          top: '45%',
          left: '10%',
          fontSize: '1.5rem',
          fontWeight: 'normal',
          fontFamily: 'monospace',
          color: '#ccc',
          textShadow: '1px 1px 2px rgba(0,0,0,0.6)',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      >
        <div
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            padding: '0.5rem 0.5rem',
            borderRadius: '8px',
            width: 'fit-content',
            marginBottom: '4px',
          }}
        >
          When the dead are no longer allowed to rest in peace,
        </div>

        <div
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            padding: '0.5rem 0.5rem',
            borderRadius: '8px',
            width: 'fit-content',
          }}
        >
          who is to blame?
        </div>
      </div>
    </div>
  );
}
